// @flow
import _ from 'lodash';

import ApiManager from './ApiManager';
import UserApiErrors from '../utils/apiErrors/UserApiErrors';
import MangoPayApiErrors from '../utils/apiErrors/MangoPayApiErrors';

// -- contants
import APIEndpoints from '../constants/APIEndpoints';

// -- entities
import {
  Restaurant,
  ApiError
} from '../entities';

import {
  UsersList,
  User
} from '../entities/user';
import CreditCard from '../entities/user/CreditCard';
import CreditCardsList from '../entities/user/CreditCardsList';
import UserAccount from '../entities/user/UserAccount';
import UserAddress from '../entities/user/UserAddress';
import UserAddressesList from '../entities/user/UserAddressesList';

import { ProDomainsList, ProAccount } from '../entities/security';
import { RestaurantsGroup } from '../entities/restaurantsGroup';

class ApiUser {

  //
  // Current user
  //

  /**
   * Log the pro user on the API. Currently, there is no specific login route,
   * we test on the /me to retrieve the user info.
   *
   * @param username
   * @param password
   *
   * TODO: rename to LoginPro
   */
  static login(username: string, password: string, apiKey: string, success: Function, failure: Function) {

    // We don't use parent 'get' method, because we want to handle the 401 in a different way.

    const options = {
      url: ApiManager.config.apiUrl + APIEndpoints.LOGIN,
      headers: {
        Accept: 'application/json',
        Authorization: ApiManager.formatAuthorizationHeader(username, password),
        'api-consumer': apiKey
      },
      success: (res) => {
        success(username, password);
      },
      failure: (error, res) => {
        const apiError: ApiError = ApiManager.createApiError(res.text);
        apiError.localizedMessage = UserApiErrors.login(apiError);
        failure(apiError);
      }
    }

    ApiManager.getExtern(options);
  }

  /**
   * Log the pro user on the API. Currently, there is no specific login route,
   * we test on the /me to retrieve the user info.
   *
   * @param username
   * @param password
   */
  static loginSimpleUser(username: string, password: string, apiKey: string, success: Function, failure: Function) {

    // We don't use parent 'get' method, because we want to handle the 401 in a different way.

    const options = {
      url: ApiManager.config.apiUrl + APIEndpoints.USER_ME,
      headers: {
        Accept: 'application/json',
        Authorization: ApiManager.formatAuthorizationHeader(username, password, 'user'),
        'api-consumer': apiKey
      },
      success: (res) => {
        const user: User = new User().fromJson(JSON.parse(res.text));

        success(user, username, password);
      },
      failure: (error, res) => {
        const apiError: ApiError = ApiManager.createApiError(res.text);
        apiError.localizedMessage = UserApiErrors.login(apiError);
        failure(apiError);
      }
    }

    ApiManager.getExtern(options);
  }

  //
  // Users
  //

  /**
   * Retrieve a list of users with the given users ids.
   * Note that a not found user will be given as an empty user object.
   *
   * @param usersIds
   * @param failureCallback
   */
   static getUsers(usersIds: Array<String>, success: Function, failure: Function) {
     if (_.isEmpty(usersIds)) {
       console.log('[USERS] array of ids empty');
       success(new UsersList());
       return;
     }

     const requestOptions = {
       endpoint: APIEndpoints.USERS,
       query: {
         ids: _.join(_.uniq(usersIds), ',')
       },
       success: (json: Object) => {
         console.log('[USERS] SUCCESS: ', json);

         const usersList: UsersList = new UsersList();
         usersList.fromJson(json);

         success(usersList);
       },
       failure: (apiError: ApiError) => {
         console.log('[USERS] FAILURE: ', apiError);
         failure(apiError);
       }
     }

     ApiManager.get(requestOptions);
   }

  //
  // Get single user
  //

  /**
   * Get the user's information for the logged in restaurant.
   * @param userId
   */
  static getUser(userId: string, success: Function, failure: Function) {
    const endpoint = APIEndpoints.USER.replace(':userId', userId);

    const requestOptions = {
      endpoint,
      success: (json: Object) => {
        console.log('[USERS] SUCCESS: ', json);

        const user: User = new User();
        user.fromJson(json);

        success(user);
      },
      failure: (apiError: ApiError) => {
        console.log('[USERS] FAILURE: ', apiError);
        failure(apiError);
      }
    }

    ApiManager.get(requestOptions);
  }

  /**
   * Retrieve the user's information for a payment.
   * For now, it is used to retrieve the user's information for the logged in restaurant.
   * See UserModal, SummaryPage, ReviewView.
   *
   * @param userId
   * @param paymentId
   */
  static getUserForPayment(userId: string, paymentId: string, success: Function, failure: Function) {
    const endpoint = APIEndpoints.USER.replace(':userId', userId);

    const requestOptions = {
      endpoint,
      query: {
        paymentId
      },
      success: (json: Object) => {
        console.log('[USERS FOR PAYMENT] SUCCESS: ', json);

        const user: User = new User();
        user.fromJson(json);

        success(user);
      },
      failure: (apiError: ApiError) => {
        console.log('[USERS FOR PAYMENT] FAILURE: ', apiError);
        failure(apiError);
      }
    }

    ApiManager.get(requestOptions);
  }

  /**
   * Retrieve the user's information for a review.
   * For now, it is used to retrieve the user's information for the logged in restaurant.
   * See UserModal, SummaryPage, ReviewView.
   *
   * @param userId
   * @param paymentId
   */
  static getUserForReview(userId: string, reviewId: string, success: Function, failure: Function) {
    const endpoint = APIEndpoints.USER.replace(':userId', userId);

    const requestOptions = {
      endpoint,
      query: {
        reviewId
      },
      success: (json: Object) => {
        console.log('[USERS FOR PAYMENT] SUCCESS: ', json);

        const user: User = new User();
        user.fromJson(json);

        success(user);
      },
      failure: (apiError: ApiError) => {
        console.log('[USERS FOR PAYMENT] FAILURE: ', apiError);
        failure(apiError);
      }
    }

    ApiManager.get(requestOptions);
  }


  //
  // Create user
  //

  static postUser(userToCreate: Object, success: Function, failure: Function) {
    const endpoint = APIEndpoints.USERS;

    const requestOptions = {
      endpoint,
      data: userToCreate,
      success: (json: Object) => {
        console.log('[POST USER] SUCCESS: ', json);

        const user = new User().fromJson(json)
        user.generateRegisterId()

        success(user, userToCreate.email, userToCreate.password);
      },
      failure: (apiError: ApiError) => {
        console.log('[POST USER] FAILURE: ', apiError);
        apiError.localizedMessage = UserApiErrors.postUser(apiError);
        failure(apiError);
      }
    }

    ApiManager.post(requestOptions);
  }

  static postUserWithoutSmsConfirmation(userToCreate: Object, success: Function, failure: Function) {
    const endpoint = APIEndpoints.USERS;

    const requestOptions = {
      endpoint,
      data: userToCreate,
      query: {
        sendPhoneVerificationCode: false
      },
      success: (json: Object) => {
        console.log('[POST USER] SUCCESS: ', json);

        const user = new User().fromJson(json)
        user.generateRegisterId()

        success(user, userToCreate.email, userToCreate.password);
      },
      failure: (apiError: ApiError) => {
        console.log('[POST USER] FAILURE: ', apiError);
        apiError.localizedMessage = UserApiErrors.postUser(apiError);
        failure(apiError);
      }
    }

    ApiManager.post(requestOptions);
  }

  static postUserWithoutEmailAndSmsConfirmation(userToCreate: Object, success: Function, failure: Function) {
    const endpoint = APIEndpoints.USERS;

    const requestOptions = {
      endpoint,
      data: userToCreate,
      query: {
        sendPhoneVerificationCode: false,
        sendRegistrationEmail: false,
      },
      success: (json: Object) => {
        console.log('[POST USER] SUCCESS: ', json);

        const user = new User().fromJson(json)
        user.generateRegisterId()

        success(user, userToCreate.email, userToCreate.password);
      },
      failure: (apiError: ApiError) => {
        console.log('[POST USER] FAILURE: ', apiError);
        apiError.localizedMessage = UserApiErrors.postUser(apiError);
        failure(apiError);
      }
    }

    ApiManager.post(requestOptions);
  }

  static postUserLightWithoutEmailAndSmsConfirmation(userToCreate: Object, apiKey: string, success: Function, failure: Function) {
    const endpoint = APIEndpoints.USERS;

    const requestOptions = {
      endpoint,
      data: userToCreate,
      query: {
        isUserLight: true,
        sendPhoneVerificationCode: false,
        sendRegistrationEmail: false,
      },
      success: (json: Object) => {
        console.log('[POST USER LIGHT] SUCCESS: ', json);

        success(userToCreate);
      },
      failure: (apiError: ApiError) => {
        if (apiError.code === "107") {
          success(userToCreate);
          return;
        }
        console.log('[POST USER LIGHT] FAILURE: ', apiError);
        apiError.localizedMessage = UserApiErrors.postUser(apiError);
        failure(apiError);
      }
    }

    ApiManager.post(requestOptions);
  }


  static postUserForRestaurantsGroup(userToCreate: Object, success: Function, failure: Function) {
    const endpoint = APIEndpoints.USERS;

    const requestOptions = {
      endpoint,
      data: userToCreate,
      query: {
        sendPhoneVerificationCode: false,
        sendRegistrationEmail: false,
      },
      success: (json: Object) => {
        console.log('[POST USER] SUCCESS: ', json);

        success(new User().fromJson(json), userToCreate.email, userToCreate.password);
      },
      failure: (apiError: ApiError) => {
        console.log('[POST USER] FAILURE: ', apiError);
        apiError.localizedMessage = UserApiErrors.postUser(apiError);
        failure(apiError);
      }
    }

    ApiManager.post(requestOptions);
  }


  //
  // Update user
  //

  static putUser(userId: string, userToUpdate: Object, success: Function, failure: Function) {
    const endpoint = APIEndpoints.USER.replace(':userId', userId);

    const requestOptions = {
      endpoint,
      data: userToUpdate,
      query: {
        sendPhoneVerificationCode: false,
      },
      success: (json: Object) => {
        console.log('[PUT USER] SUCCESS: ', json);
        success(new User().fromJson(json));
      },
      failure: (apiError: ApiError) => {
        console.log('[PUT USER] FAILURE: ', apiError);
        apiError.localizedMessage = UserApiErrors.putUser(apiError);
        failure(apiError);
      }
    }

    ApiManager.put(requestOptions);
  }


  //
  // -------------- Credit card
  //

  //
  // CreditCard - GET
  //

  static getUserCreditCards(userId: string, success: Function, failure: Function) {
    const endpoint = APIEndpoints.USER_CREDIT_CARDS.replace(':userId', userId);

    const requestOptions = {
      endpoint,
      query: {
      },
      success: (json: Object) => {
        console.log('[USER CREDIT CARDS] SUCCESS: ', json);

        const creditCardsList: CreditCardsList = new CreditCardsList();
        creditCardsList.fromJson(json);

        success(creditCardsList);
      },
      failure: (apiError: ApiError) => {
        console.log('[USER CREDIT CARDS] FAILURE: ', apiError);
        failure(apiError);
      }
    }

    ApiManager.get(requestOptions);
  }

  //
  // CreditCard - POST
  //

  static postUserCreditCard(userId: string, creditCard: CreditCard, success: Function, failure: Function) {
    ApiUser.registerCreditCard(userId, creditCard, (registrationId: string, registrationData: string) => {
      ApiUser.postCreditCard(userId, registrationId, registrationData, success, failure);
    }, failure);
  }

  static registerCreditCard(userId: string, creditCard: CreditCard, success: Function, failure: Function) {
    const endpoint = APIEndpoints.ME_CREDIT_CARD_REGISTRATION;
    // const endpoint = APIEndpoints.USER_CREDIT_CARD_REGISTRATION.replace(':userId', userId);



    //	the id previously returned by POST /users/{user_id}/cards-registration
    let registrationId:	string;

    // he content returned by the POST on the URL returned by the POST /users/{user_id}/cards-registration itself
    let registrationData:	string;

    const options = {
      endpoint,
      data: {
        /**
         * ISO_4217 format expected
         * @link https://www.wikiwand.com/en/ISO_4217
         */
        currency: creditCard.currency,

        /**
         * A custom short label describing the card (eg. "Biz", "Pro", "Perso", "Wife", ...)
         */
        name: creditCard.name
      },
      success: (json: Object) => {
        console.log('[REGISTER USER CREDIT CARD] SUCCESS: ', json);

        // {
        //  "id": "135813513",
        //  "data": "nqxcjnoNOZut09Q6d513q5DQ3z81z5ddZDSCXVV",
        //  "accessKeyRef": "AH546AAZ214567ZdzZD",
        //  "url": "https://payline.com/card-register"
        // }

        const { url, id, data, accessKeyRef } = json;

        registrationId = id;
        registrationData = data;

        ApiManager.postExtern({
          url,
          headers: {
            // 'Access-Control-Allow-Headers': '*',
            'Content-Type': ApiManager.ContentTypes.FORM_URL_ENCODE
          },
          data: {
            cardNumber: creditCard.getCleanNumber(),

            /**
             * should be formated as MMYY where MM are the 2 digits of the month (from 01 to 12) and YY are the 2 last digits of the year.
             */
            cardExpirationDate: creditCard.expirationDate.format('MMYY'),

            cardCvx: creditCard.cvv,

            data: registrationData,

            accessKeyRef: accessKeyRef
          },
          success: (response) => {
            let registrationData = response.text;

            //
            // The mangopay api returns a 201 but with an errorCode inside..
            //
            if (_.includes(registrationData, 'errorCode=')) {
              console.log('POST EXTERN REGISTRAION CARD FAILURE', response.text);
              // get errorCode int
              const errorCode = _.replace(registrationData, 'errorCode=', '');

              const apiError = new ApiError();
              apiError.code = errorCode.trim();
              apiError.message = 'MangoPay error';

              apiError.localizedMessage = MangoPayApiErrors.registerCreditCart(apiError);
              failure(apiError);
            } else {
              console.log('POST EXTERN REGISTRAION CARD', response.text);
              success(registrationId, registrationData);
            }

          },
          failure: (error, res) => {
            // TODO
            failure();
          }
        })


      },
      failure: (apiError: ApiError) => {
        console.log('[REGISTER USER CREDIT CARD] FAILURE: ', apiError);
        apiError.localizedMessage = UserApiErrors.postCreditCard(apiError);
        failure(apiError);
      }
    }

    ApiManager.post(options);
  }

  static postCreditCard(userId: string, registrationId: string, registrationData: string, success: Function, failure: Function) {

    /**
     * TODO: change ME for USER_CREDIT_CARDS when api updated.
     */
    const endpoint = APIEndpoints.ME_CREDIT_CARDS;

    //const endpoint = APIEndpoints.USER_CREDIT_CARDS.replace(':userId', userId); const endpoint = APIEndpoints.USER_CREDIT_CARDS.replace(':userId', userId);

    const requestOptions = {
      endpoint,
      data: {
        registrationId,
        registrationData
      },
      success: (json: Object) => {
        console.log('[POST USER CREDIT CARD] SUCCESS: ', json);

        const newCreditCard: CreditCard = new CreditCard();
        newCreditCard.fromJson(json);

        success(newCreditCard);
      },
      failure: (apiError: ApiError) => {
        console.log('[POST USER CREDIT CARD] FAILURE: ', apiError);
        apiError.localizedMessage = UserApiErrors.postCreditCard(apiError);
        failure(apiError);
      }
    }

    ApiManager.post(requestOptions);
  }

  static deleteCreditCard(userId: string, creditCard: CreditCard, success: Function, failure: Function) {
    /**
     * TODO: change ME for USER_CREDIT_CARDS when api updated.
     */
    const endpoint = APIEndpoints.ME_CREDIT_CARD.replace(':userId', userId).replace(':creditCardId', creditCard.id);

    const requestOptions = {
      endpoint,
      success: (json: Object) => {
        console.log('[DELETE USER CREDIT CARD] SUCCESS: ', json);

        success(creditCard);
      },
      failure: (apiError: ApiError) => {
        console.log('[DELETE USER CREDIT CARD] FAILURE: ', apiError);
      apiError.localizedMessage = UserApiErrors.deleteCreditCard(apiError);
        failure(apiError);
      }
    }

    ApiManager.delete(requestOptions);
  }

  //
  // -------------- Addresses
  //

  static getAddressesList(userId: string, success: Function, failure: Function) {
    const endpoint = APIEndpoints.USER_ADDRESSES.replace(':userId', userId);

    const requestOptions = {
      endpoint,
      success: (json: Object) => {
        console.log('[GET USER ADDRESSES] SUCCESS: ', json);

        const addressesList: UserAddressesList = new UserAddressesList();
        addressesList.fromJson(json);

        success(addressesList);
      },
      failure: (apiError: ApiError) => {
        console.log('[GET USER ADDRESSES] FAILURE: ', apiError);
        apiError.localizedMessage = UserApiErrors.getAddresses(apiError);
        failure(apiError);
      }
    }

    ApiManager.get(requestOptions);
  }

  static postAddress(userId: string, address: UserAddress, success: Function, failure: Function) {
    const endpoint = APIEndpoints.USER_ADDRESSES.replace(':userId', userId);

    const requestOptions = {
      endpoint,
      data: address,
      success: (json: Object) => {
        console.log('[POST USER ADDRESS] SUCCESS: ', json);

        const newAddress: UserAddress = new UserAddress();
        newAddress.fromJson(json);

        success(newAddress);
      },
      failure: (apiError: ApiError) => {
        console.log('[POST USER ADDRESS] FAILURE: ', apiError);
        apiError.localizedMessage = UserApiErrors.postAddress(apiError);
        failure(apiError);
      }
    }

    ApiManager.post(requestOptions);
  }

  static putAddress(userId: string, addressToUpdate: UserAddress, success: Function, failure: Function) {
    const endpoint = APIEndpoints.USER_ADDRESS.replace(':userId', userId).replace(':addressId', addressToUpdate.id);

    const requestOptions = {
      endpoint,
      data: addressToUpdate,
      success: (json: Object) => {
        console.log('[PUT USER ADDRESS] SUCCESS: ', json);

        const address: UserAddress = new UserAddress();
        address.fromJson(json);

        success(address);
      },
      failure: (apiError: ApiError) => {
        console.log('[PUT USER ADDRESS] FAILURE: ', apiError);
        apiError.localizedMessage = UserApiErrors.putAddress(apiError);
        failure(apiError);
      }
    }

    ApiManager.put(requestOptions);
  }

  static deleteAddress(userId: string, address: UserAddress, success: Function, failure: Function) {
    const endpoint = APIEndpoints.USER_ADDRESS.replace(':userId', userId).replace(':addressId', address.id);

    const requestOptions = {
      endpoint,
      success: (json: Object) => {
        console.log('[DELETE USER ADDRESS] SUCCESS: ', json);

        success(address);
      },
      failure: (apiError: ApiError) => {
        console.log('[DELETE USER ADDRESS] FAILURE: ', apiError);
        apiError.localizedMessage = UserApiErrors.deleteAddress(apiError);
        failure(apiError);
      }
    }

    ApiManager.delete(requestOptions);
  }

  static getRecoverPassword(email: string, apiKey: string, success: Function, failure: Function) {
    const endpoint = APIEndpoints.USER_RECOVER_PASSWORD.replace(':userEmail', email);

    const requestOptions = {
      endpoint,
      headers: {
        'api-consumer': apiKey
      },
      success: (json: Object) => {
        console.log('[POST RECOVER PASSWORD] SUCCESS: ', json);

        success();
      },
      failure: (apiError: ApiError) => {
        console.log('[POST RECOVER PASSWORD] FAILURE: ', apiError);
        failure(apiError);
      }
    }

    ApiManager.get(requestOptions);
  }

  static getUserAccounts(userId: string, success: Function, failure: Function) {
    const endpoint = APIEndpoints.USER_ACCOUNTS.replace(':userId', userId);

    const requestOptions = {
      endpoint,
      success: (json: Object) => {
        console.log('[GET USER ACCOUNTS] SUCCESS: ', json);

        let userAccounts = []
        userAccounts = json.map(userAccountJson => {
          const userAccount = new UserAccount()
          return userAccount.fromJson(userAccountJson)
        })

        success(userAccounts);
      },
      failure: (apiError: ApiError) => {
        console.log('[GET USER ACCOUNTS] FAILURE: ', apiError);
        failure(apiError);
      }
    }

    ApiManager.get(requestOptions);
  }


}

export default ApiUser;



// WEBPACK FOOTER //
// ./sdk/scripts/api/ApiUser.js